import { makeStyles } from "tss-react/mui"
import { MenuItem, Stack } from "@mui/material"
import { StandardDialog } from "../../../../../../../../shared/components/dialogs/StandardDialog"
import { LocalizedEnum, LocalizedStrict } from "../../../../../../../localization/components/AppLocalized"
import { ReminderType, ResourceNotification, ResourceNotificationForm } from "../../../resourcesTypes"
import { REMINDER_OPTIONS } from "../../../constants"
import {
    FormikDropdown,
    FormikSubmitButton,
    FormikTextField,
} from "../../../../../../../../shared/components/forms/Formik"
import { iterateStringEnum } from "../../../../../../../../lib/enums"
import { Form, Formik } from "formik"
import * as Yup from "yup"
import { useAppLocalization } from "../../../../../../../localization/components/AppLocalizationProvider"
import { Box } from "@mui/system"
import { TranslationKey } from "../../../../../../../localization/translations"
import { MAX_ORDER_COMMENT_LENGTH } from "../../../../../../../../constants/inputValidationDefaults"
import { getFormInitialValue, NotificationFormValues } from "./utils"

const useStyles = makeStyles()(() => ({
    container: {
        padding: "2rem 4rem",
    },
}))

type AddReminderDialogProps = Readonly<{
    onAdd: (value: ResourceNotificationForm) => void
    open: boolean
    onClose: () => void
    notifications: ResourceNotification[]
    notification: ResourceNotification | null
}>

function generateUniqueId(array: ResourceNotification[], min: number = 1, max: number = 10000): number {
    let id: number
    do {
        id = Math.floor(Math.random() * (max - min + 1)) + min
    } while (array.some((item) => item.id === id))
    return id
}

export function AddReminderDialog({ open, notifications, notification, onClose, onAdd }: AddReminderDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const initForm = getFormInitialValue(notification)

    const validationSchema = Yup.object({
        message: Yup.string().max(
            MAX_ORDER_COMMENT_LENGTH,
            l10n.getString("tab-availability-add-reminder-dialog-text-max", { max: MAX_ORDER_COMMENT_LENGTH }),
        ),
    })

    const onSubmit = (values: NotificationFormValues) => {
        const timeKey = values.reminderTime > 0 ? "sendInMinutesAfterMeeting" : "sendMinutesBeforeMeeting"

        const newItem: ResourceNotificationForm = {
            id: generateUniqueId(notifications),
            message: values.message || "",
            type: values.type,
            [timeKey]: Math.abs(values.reminderTime),
        }

        onAdd(newItem)
    }

    const getTime = (minutes: number) => {
        const abs = Math.abs(minutes)
        const isHour = abs % 60 === 0

        return isHour ? abs / 60 : abs
    }

    const getSuffixId = (value: number): TranslationKey => {
        const isBefore = value < 0
        const direction = isBefore ? "before" : "after"
        const unitPart = Math.abs(value) < 60 ? "minutes" : "hours"

        return `tab-availability-add-reminder-dialog-form-time-suffix-${unitPart}-${direction}`
    }

    return (
        <StandardDialog
            dialogTitle={l10n.getString(
                notification
                    ? "tab-availability-add-reminder-edit-dialog-title"
                    : "tab-availability-add-reminder-dialog-title",
            )}
            open={open}
            onClose={onClose}
            maxWidthProp="sm"
            scroll="body"
        >
            <Formik<NotificationFormValues>
                initialValues={initForm}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {({ values, handleSubmit }) => {
                    const config = REMINDER_OPTIONS[values.type]
                    return (
                        <Box component={Form} onSubmit={handleSubmit}>
                            <Stack className={classes.container} spacing="2rem">
                                <LocalizedStrict
                                    id="tab-availability-add-reminder-dialog-field-type-label"
                                    attrs={{ label: true }}
                                >
                                    <FormikDropdown name="type" label="Type" fullWidth>
                                        {[...iterateStringEnum(ReminderType)].map((type) => (
                                            <MenuItem key={type} value={type}>
                                                <LocalizedEnum base="reminder-type-option" enumValue={type}>
                                                    <span>{type}</span>
                                                </LocalizedEnum>
                                            </MenuItem>
                                        ))}
                                    </FormikDropdown>
                                </LocalizedStrict>
                                {config.showDropdown && config.dropdownOptions && (
                                    <LocalizedStrict
                                        id="tab-availability-add-reminder-dialog-field-time-label"
                                        attrs={{ label: true }}
                                    >
                                        <FormikDropdown name="reminderTime" label="Sent" fullWidth>
                                            {config.dropdownOptions.map((value) => (
                                                <MenuItem key={value} value={value}>
                                                    {getTime(value)} {l10n.getString(getSuffixId(value))}
                                                </MenuItem>
                                            ))}
                                        </FormikDropdown>
                                    </LocalizedStrict>
                                )}

                                {config.showTextField && (
                                    <LocalizedStrict
                                        id="tab-availability-add-reminder-dialog-field-text-label"
                                        attrs={{ label: true }}
                                    >
                                        <FormikTextField fullWidth name="message" label="Text" />
                                    </LocalizedStrict>
                                )}

                                <Stack direction="row" justifyContent="center">
                                    <LocalizedStrict id="tab-availability-add-reminder-dialog-submit-button-label">
                                        <FormikSubmitButton fullWidth={false} variant="contained">
                                            Add
                                        </FormikSubmitButton>
                                    </LocalizedStrict>
                                </Stack>
                            </Stack>
                        </Box>
                    )
                }}
            </Formik>
        </StandardDialog>
    )
}
